<template>
  <b-card
    :title="translations.addGift[lang]"
    @cancel="$emit('close')"
    @submit="sendForm"
    :error-message="errorMessage"
    :disabled="mode === 'create' ? !formValid : false"
    :loading="loading"
    :submit-text="{ el: 'Αποθήκευση', en: 'Save', it: 'Salva' }"
    :cancel-text="{ el: 'Ακύρωση', en: 'Cancel', it: 'Cancella' }"
  >
    <b-text-field
      :label="translations.name[lang]"
      v-model="giftData.title"
      no-margin-top
      :success="success.title"
      :error-messages="error.title"
      @focus="error.title = ''"
      @blur="validateTitle"
    ></b-text-field>

    <b-textarea
      :label="translations.description[lang]"
      v-model="giftData.description"
      :success="success.description"
      :error-messages="error.description"
      @focus="error.description = ''"
      @blur="validateDescription"
    ></b-textarea>

    <b-text-field
      :label="translations.catalogCode[lang]"
      v-model="giftData.printed_catalog_code"
    ></b-text-field>

    <b-text-field
      :label="translations.giftCode[lang]"
      v-model="giftData.product_code"
    ></b-text-field>

    <h4 v-if="mode == 'create'" class="subtitle-2 mt-3">
      {{ translations.giftType[lang] }}
    </h4>
    <v-radio-group
      v-if="mode == 'create'"
      v-model="giftData.gift_type_id"
      class="mt-3 pt-0"
      hide-details
      :success="success.gift_type"
      :error-messages="error.gift_type"
      @focus="error.gift_type = ''"
      @blur="validateGiftType"
    >
      <v-row no-gutters>
        <v-col class="ml-2" cols="auto" v-for="(type, index) in giftTypes" :key="index">
          <v-radio color="secondary" :value="type.gift_type_id">
            <template v-slot:label>
              <h4 class="subtitle-2">{{ type.name[lang] }}</h4>
            </template>
          </v-radio>
        </v-col>
      </v-row>
    </v-radio-group>

    <b-select
      v-if="selectedGiftType.system == 'product'"
      :items="suppliers"
      item-text="name"
      item-value="supplier_store_id"
      v-model="giftData.supplier_store_id"
      :label="translations.supplier[lang]"
      :success="success.supplier"
      :error-messages="error.supplier"
      @focus="error.supplier = ''"
      @blur="validateSupplier"
    ></b-select>
    <div v-if="selectedGiftType.system == 'voucher'">
      <v-switch
        v-model="redeemAllStores"
        color="secondary"
        class="mt-3 mb-3 pt-0"
        hide-details="auto"
        :label="
          redeemAllStores
            ? translations.redeemAllStores[lang]
            : translations.redeemSpecificStores[lang]
        "
      ></v-switch>
      <v-autocomplete
        v-if="selectedGiftType.system == 'voucher'"
        :disabled="!!redeemAllStores"
        :items="redeemStoresSelections"
        :label="translations.redeemStores[lang]"
        color="black"
        outlined
        multiple
        menu-props="offsetY"
        item-color="secondary"
        dense
        open-on-clear
        hide-details="auto"
        v-model="giftData.redeem_stores"
        item-text="app_name"
        item-value="store_id"
        required
        class="pt-0"
        clearable
        deletable-chips
        small-chips
        :loading="companyStoresLoading"
        validate-on-blur
        :search-input.sync="search"
      >
        <template v-slot:append-item>
          <div v-intersect="endIntersect"></div>
        </template>
      </v-autocomplete>
    </div>

    <b-select
      :items="giftCategories"
      :item-text="`name[${lang}]`"
      item-value="gift_category_id"
      v-model="giftData.gift_category_id"
      :label="translations.giftCategory[lang]"
      :success="success.gift_category"
      :error-messages="error.gift_category"
      @focus="error.gift_category = ''"
      @blur="validateGiftCategory"
    ></b-select>

    <b-text-field
      :label="translations.giftPrice[lang]"
      v-model="giftData.cost"
      type="number"
      :success="success.cost"
      :error-messages="error.cost"
      @focus="error.cost = ''"
      @blur="validateCost"
    ></b-text-field>

    <v-radio-group
      v-if="selectedGiftType.system == 'voucher'"
      v-model="giftData.cost_type"
      class="mt-3 pt-0"
      hide-details
    >
      <v-row no-gutters>
        <v-col class="ml-2" cols="auto">
          <v-radio color="secondary" value="amount" :label="translations.amount[lang]" />
        </v-col>
        <v-col class="ml-2" cols="auto">
          <v-radio color="secondary" value="percentage" :label="translations.percentage[lang]" />
        </v-col>
      </v-row>
    </v-radio-group>

    <b-text-field
      :label="translations[storeMode][lang]"
      type="number"
      v-model="giftData.points"
      :success="success.points"
      :error-messages="error.points"
      @focus="error.points = ''"
      @blur="validatePoints"
    ></b-text-field>
    <v-autocomplete
      :items="loyaltiesData"
      v-model="giftData.loyalty_program_ids"
      :item-text="`name[${lang}]`"
      item-value="loyalty_program_id"
      menu-props="offsetY"
      :label="translations.loyalties[lang]"
      class="mt-4"
      outlined
      dense
      hide-details
      clearable
      multiple
      :search-input.sync="searchLoyalties"
      :loading="loading"
      :disable-lookup="true"
      item-disabled="disable"
    >
      <template v-slot:selection="data">
        <v-chip
          v-if="gift.loyalty_program_ids.length < 5"
          v-bind="data.attrs"
          close
          small
          class="mt-1"
          @click:close="removeSelectedLoyalty(data)"
        >
          {{ data.item.name[lang] }}
        </v-chip>
        <div v-else-if="data.index === 0">
          {{ `${gift.loyalty_program_ids.length} ` + translations.loyaltiesSelected[lang] }}
        </div>
      </template>
      <template v-slot:append-item>
        <div v-intersect="endIntersectLoyalties"></div>
        <v-list-item v-if="loading">
          <v-list-item-action class="ml-1">
            <v-row class="d-flex align-center">
              <v-btn
                class="text-capitalize"
                depressed
                fab
                small
                :loading="true"
                style="background: transparent"
              >
              </v-btn>
              <span class="pa-2">{{ translations.loadMore[lang] }}</span>
            </v-row>
          </v-list-item-action>
        </v-list-item>
      </template>
    </v-autocomplete>
    <v-switch
      v-model="tierSwitch"
      color="secondary"
      class="mt-3 pt-0"
      hide-details="auto"
      @change="validateTier"
      :label="translations.tierSwitchDescription[lang]"
    ></v-switch>
    <b-select
      v-if="tierSwitch"
      :items="tiers"
      :item-text="`title[${lang}]`"
      item-value="store_point_title_id"
      v-model="giftData.store_point_title_id"
      :label="translations.selectTiers[lang]"
      :success="success.tier"
      :error-messages="error.tier"
      @focus="error.tier = ''"
      @change="validateTier"
    ></b-select>
    <v-checkbox color="secondary" class="mt-3 pt-0" hide-details v-model="giftData.show_on_catalog">
      <template v-slot:label>
        <h4 class="subtitle-2">{{ translations.showGift[lang] }}</h4>
      </template>
    </v-checkbox>
    <v-checkbox
      v-if="selectedGiftType.system == 'voucher'"
      color="secondary"
      class="mt-3 pt-0"
      hide-details
      v-model="hasSynergies"
    >
      <template v-slot:label>
        <h4 class="subtitle-2">
          {{ translations.hasSynergies[lang] }}
        </h4>
      </template>
    </v-checkbox>
    <v-col cols="12" class="pr-2" v-if="hasSynergies">
      <div v-if="hasSynergies">
        <v-row v-for="synergy in synergiesData" :key="synergy.company_id">
          <v-col cols="12" class="d-flex align-center">
            <v-checkbox
              hide-details
              color="black"
              class="mt-3 pt-0"
              v-model="synergy.enabled"
              @change="selectStores(synergy)"
            >
              <template v-slot:label>
                <h4 class="subtitle-2">{{ synergy.name }}</h4>
              </template>
            </v-checkbox>
          </v-col>
          <v-autocomplete
            :disabled="!synergy.enabled"
            class="mt-0 pt-0 pl-12"
            :items="synergy.stores"
            :search-input.sync="searchSynergies"
            :label="translations.chooseSynergyStores[lang]"
            :loading="loading"
            item-color="secondary"
            color="black"
            autocomplete
            single-line
            v-model="synergy.exclusive_stores"
            item-text="name"
            item-value="store_id"
            multiple
          >
            <template v-slot:selection="{ item }">
              <template>
                <v-list-item-content
                  v-if="!!synergy.exclusive_stores && item.store_id === synergy.exclusive_stores[0]"
                >
                  <v-list-item-title
                    v-if="
                      !!synergy.exclusive_stores &&
                      synergy.exclusive_stores.length === synergy.stores.length
                    "
                    v-html="
                      translations.allStoresAreSelected[lang] +
                      ' (' +
                      synergy.exclusive_stores.length +
                      ')'
                    "
                  >
                  </v-list-item-title>
                  <v-list-item-title
                    v-if="
                      !!synergy.exclusive_stores &&
                      synergy.exclusive_stores.length !== synergy.stores.length
                    "
                    v-html="
                      !!synergy.exclusive_stores
                        ? translations.storesSelected[lang] + ': ' + synergy.exclusive_stores.length
                        : ''
                    "
                  >
                  </v-list-item-title>
                </v-list-item-content>
              </template>
            </template>
            <template v-slot:prepend-item>
              <v-list-item ripple @click="selectStores(synergy, true)">
                <v-list-item-action>
                  <v-icon :color="synergy.exclusive_stores.length > 0 ? 'indigo darken-4' : ''">
                    {{ iconSelectIcon(synergy) }}
                  </v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>
                    <v-row no-gutters>
                      <v-col cols="6">
                        {{
                          synergy.exclusive_stores.length === synergy.stores.length
                            ? translations.diselectAll[lang]
                            : translations.selectAll[lang]
                        }}
                      </v-col>
                      <v-col cols="6" align="right">
                        <v-icon
                          color="'indigo darken-4'"
                          @click.prevent.self.stop="toggleSelect = false"
                        >
                          <!-- mdi-close-box -->
                        </v-icon>
                      </v-col>
                    </v-row>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <v-divider></v-divider>
            </template>
            <template v-slot:item="data">
              <template>
                <v-list-item-content>
                  <v-list-item-title v-html="data.item.name"></v-list-item-title>
                </v-list-item-content>
              </template>
            </template>
          </v-autocomplete>
        </v-row>
      </div>
    </v-col>

    <v-file-input
      class="mt-4"
      :label="translations.giftImage[lang]"
      color="secondary"
      outlined
      dense
      hide-details
      v-model="uploadingImage"
    ></v-file-input>

    <div v-if="gift.image" class="mt-4">
      {{ translations.lastUploaded[lang] }}
      <a :href="gift.image">{{ gift.image.substr(0, 40) }}...</a>
    </div>
  </b-card>
</template>

<script>
import { mdiPlus } from '@mdi/js';
import { mapMutations, mapState, mapActions } from 'vuex';
import validators from '@/utils/validators/loyaltyPanel/gifts';
import translations from '@/utils/translations/loyaltyPanel/giftCatalog';
import { cloneDeep, debounce } from 'lodash';

export default {
  name: 'GiftForm',

  mixins: [validators, translations],

  props: {
    mode: String,
  },

  data: () => ({
    icons: {
      mdiPlus,
    },
    redeemAllStores: true,
    tierSwitch: false,
    search: '',
    searchSynergies: '',
    storeMenu: false,
    hasSynergies: false,
    synergiesData: [],
    redeemStoresData: [],
    page: 1,
    searchLoyalties: '',
    selectedLoyaltiesData: [],
    loyaltyPage: 1,
    giftData: {},
  }),
  computed: {
    ...mapState(['loading', 'errorMessage', 'serverItemsPerPage']),
    ...mapState('loyaltyPanel/catalogManagement', ['giftTypes', 'suppliers', 'synergies', 'gift']),
    ...mapState('loyaltyPanel', ['companyStores', 'companyStoresLoading']),
    ...mapState('loyaltyPanel/pointTitles', ['tiers']),

    giftCategories: {
      get() {
        return this.$store.state.loyaltyPanel.catalogManagement.giftCategories;
      },
    },

    lang() {
      return this.$route.params.lang;
    },
    storeMode() {
      return this.$store.state.store?.display_mode || 'points';
    },
    title() {
      return this.mode === 'create'
        ? this.translations.newGiftTitle[this.lang]
        : this.translations.updateGiftTitle[this.lang];
    },

    selectedGiftType() {
      return this.giftTypes.find((x) => x.gift_type_id == this.giftData.gift_type_id) || {};
    },

    uploadingImage: {
      get() {
        return this.$store.state.loyaltyPanel.catalogManagement.uploadingImage;
      },
      set(val) {
        this.setUploadingImage(val);
      },
    },
    query() {
      if (this.search) {
        return `?q=${this.search}&page=${this.page}`;
      }

      return `?page=${this.page}`;
    },
    synergyStores() {
      return this.synergies.reduce(
        (a, c) => [
          ...a,
          { divider: c.company_id !== this.synergies[0].company_id },
          {
            ...c,
            store_id: c.company_id,
            disabled: false,
            collapsed: false,
          },
          ...c.stores.map((e) => (e = { ...e, company_id: c.company_id })),
        ],
        []
      );
    },
    redeemStoresSelections() {
      let stores = cloneDeep(this.companyStores);
      this.redeemStoresData.forEach((redeemStore) => {
        if (!stores.find((store) => store.store_id === redeemStore.store_id)) {
          stores.push(redeemStore);
        }
      });
      return stores;
    },
    loyalties() {
      return this.$store.state.loyaltyPanel.loyalties;
    },

    loyaltiesPagination() {
      return this.$store.state.loyaltyPanel.loyaltiesPagination;
    },
    loyaltiesData() {
      return [...this.loyalties, ...this.selectedLoyaltiesData];
    },
    loyaltyQuery() {
      if (this.searchLoyalties) {
        return `?q=${this.searchLoyalties}&page=${this.loyaltyPage}`;
      }

      return `?page=${this.loyaltyPage}`;
    },
  },
  watch: {
    redeemAllStores(val) {
      if (val === true) this.giftData.redeem_stores = [];
    },
    menu(val) {
      val &&
        setTimeout(() => ((this.$refs.picker.activePicker = 'YEAR'), (this.pickerDate = null)));
    },

    search() {
      this.page = 1;
      this.loadStores({
        query: this.query,
        page: this.page,
      });
    },

    successCode(val) {
      if (val) {
        this.selectedNotifications = [];
        this.selectAll = this.translations.selectAll[this.lang];
        this.search = '';
      }
    },
    hasSynergies(val) {
      if (!val) {
        this.giftData.synergies = [];
        this.setSynergiesData();
      }
    },
    async customerData(val) {
      if (val.store_id) {
        await this.loadStores({
          page: this.page,
          query: `?store=${val.store_id}`,
        });
        this.search = this.companyStores.length > 0 ? this.companyStores[0].name : '';
      } else {
        this.loadStores({
          page: this.page,
          query: this.query,
        });
      }
      this.selectedNotifications = this.customerData.notifications
        ? Object.entries(this.customerData.notifications)
            .map(([k, v], i) => (v ? k : false))
            .filter((o) => o)
        : [];
      if (this.selectedNotifications.length === 0) {
        this.customerData.marketing = false;
      }
      this.$refs.places.clearInput();
    },

    synergies: {
      handler(val, oldVal) {
        this.setSynergiesData();
      },

      deep: true,
    },
    synergiesData: {
      handler(val, oldVal) {
        if (this.hasSynergies) {
          this.giftData.synergies = val.reduce((a, c) => {
            if (c.enabled) {
              return [
                ...a,
                {
                  company_id: c.company_id,
                  exclusive_stores: c.exclusive_stores || [],
                },
              ];
            } else {
              return a;
            }
          }, []);
        } else {
          // this.giftData.synergies = []
        }
      },

      deep: true,
    },
    giftData: {
      handler(val, oldVal) {
        if (val.gift_id !== oldVal.gift_id) {
          this.setGiftSynergies();
        }
        this.tierSwitch = !!val.store_point_title_id;
      },
      deep: true,
    },
    tierSwitch: {
      handler(val, oldVal) {
        if (oldVal && !val && !!this.giftData.store_point_title_id) {
          this.giftData.store_point_title_id = null;
        }
      },
    },

    gift: {
      handler(val, oldVal) {
        this.giftData = val ? cloneDeep(val) : {};
      },
      immediate: true,
      deep: true,
    },

    ['giftData.redeem_stores']: {
      async handler(val, oldVal) {
        if (val?.length) {
          this.redeemAllStores = false;
          for (let redeemStoreId of this.giftData.redeem_stores) {
            if (!this.redeemStoresData.find((store) => store.store_id === redeemStoreId)) {
              this.redeemStoresData.push(await this.loadStore(redeemStoreId));
            }
          }
        }
      },
      deep: true,
      immediate: true,
    },

    selectedGiftType: {
      immediate: true,
      handler(val, oldVal) {
        if (val.system == 'product') {
          this.redeemAllStores = false;
          this.giftData.cost_type = undefined;
        } else if (val.system == 'voucher') {
          this.giftData.supplier_store_id = undefined;
          if (this.mode == 'create') {
            this.giftData.cost_type = this.giftData.cost_type || 'amount';
          }
        }
      },
      deep: true,
    },
    searchLoyalties() {
      this.loyaltyPage = 1;
      this.debouncedSearchLoyalties();
    },
  },
  methods: {
    ...mapActions('loyaltyPanel/catalogManagement', [
      'getGiftCategories',
      'getGiftData',
      'create',
      'update',
      'getGiftTypes',
      'getSuppliers',
      'loadSynergies',
    ]),
    ...mapMutations('loyaltyPanel/catalogManagement', ['setGift', 'setUploadingImage']),
    ...mapActions('loyaltyPanel', ['loadStores', 'loadStore', 'getLoyalties']),
    ...mapActions('loyaltyPanel/pointTitles', ['fetchPointTitles']),
    sendForm() {
      this[this.mode](this.giftData);
      this.$emit('close');
    },

    loadMoreStores() {
      this.page++;
      this.loadStores({
        query: this.query,
        page: this.page,
      });
    },
    iconSelectIcon(synergy) {
      if (!synergy.exclusive_stores) return 'mdi-checkbox-blank-outline';

      if (synergy.exclusive_stores.length === synergy.stores.length) return 'mdi-close-box';
      if (
        synergy.exclusive_stores.length < synergy.stores.length &&
        synergy.exclusive_stores.length > 0
      )
        return 'mdi-minus-box';
      return 'mdi-checkbox-blank-outline';
    },
    handleSearch() {
      this.loadStores({
        query: `?q=${this.search}`,
        page: this.page,
      });
    },
    endIntersect(entries, observer, isIntersecting) {
      if (
        isIntersecting &&
        this.companyStores &&
        this.companyStores.length >= this.serverItemsPerPage
      ) {
        this.loadMoreStores();
      }
    },
    removeSelectedLoyalty(data) {
      this.product.loyalty_program_ids.splice(data.index, 1);
    },
    selectStores(synergy, all = false) {
      if (synergy.enabled) {
        if (
          all &&
          synergy.exclusive_stores &&
          synergy.exclusive_stores.length === synergy.stores.length
        ) {
          synergy.exclusive_stores = [];
          return;
        }
        if (all || !synergy.exclusive_stores || !synergy.exclusive_stores.length) {
          synergy.exclusive_stores = synergy.stores.map((e) => e.store_id);
          return;
        }
      }
    },
    setGiftSynergies() {
      this.hasSynergies =
        this.giftData.gift_type_id === 2 &&
        !!this.giftData.synergies &&
        this.giftData.synergies.length;
      if (!!this.synergiesData && !!this.synergiesData.length) {
        for (let syn of this.synergiesData) {
          let giftSyn = this.giftData.synergies.find((x) => x.company_id === syn.company_id);
          syn.enabled = !!giftSyn;
          syn.exclusive_stores = (giftSyn && giftSyn.exclusive_stores) || [];
        }
      }
    },
    setSynergiesData() {
      this.synergiesData = this.synergies.map((e) => {
        let giftSynergy =
          this.giftData.synergies &&
          this.giftData.synergies.find((x) => x.company_id === e.company_id);
        return {
          ...e,
          enabled: !!giftSynergy,
          exclusive_stores: e.stores
            .filter((s) => giftSynergy && giftSynergy.exclusive_stores.includes(s.store_id))
            .map((x) => x.store_id),
        };
      });
    },
    endIntersectLoyalties(entries, observer, isIntersecting) {
      if (isIntersecting && this.loyalties?.length < this.loyaltiesPagination.total) {
        this.loadMoreLoyalties();
      }
    },

    async loadMoreLoyalties() {
      this.loyaltyPage++;
      const body = {
        query: this.loyaltyQuery,
        page: this.loyaltyPage,
      };
      await this.getLoyalties(body);
    },
    handleSearchLoyalties() {
      const body = {
        query: this.loyaltyQuery,
        page: this.loyaltyPage,
      };
      this.getLoyalties(body);
    },
  },
  created() {
    this.debouncedSearchLoyalties = debounce(this.handleSearchLoyalties, 500);
  },
  async mounted() {
    this.getGiftCategories();
    this.getGiftTypes();
    this.getSuppliers();
    this.loadStores({
      page: this.page,
      query: this.query,
    });
    if (!this.tiers?.length) {
      this.fetchPointTitles();
    }
    await this.loadSynergies();
    await this.setGiftSynergies();
  },
};
</script>

<style scoped></style>
